import { Alert, AnchorButton, LinkButton } from '@cluey/cluey-components';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { submitCaseToSF } from '../../../../actions/hub/hubContactPageActions';
import { removeCancelledSession } from '../../../../actions/hub/sessionsActions';
import { ContainerLayout } from '../../../../common/hub/ContainerLayout';
import EnrolmentLayout from '../../../../common/hub/EnrolmentLayout';
import { IntentIntro } from '../../../../components/IntentIntro';
import { Loader } from '../../../../components/Loader/Loader';
import { useGetSessionPolicy } from '../../../../hooks/queries/useSessionQuery';
import useRedirectInvalidSession from '../../../../hooks/session/useRedirectInvalidSession';
import { getUpcomingSessions } from '../../../../selectors/hubSelector';
import { PAYMENT_STATUS, STUDENT_SESSION_TYPE_KEYS } from '../../../../types/hubEnums';
import type { FormFlowProps, Session } from '../../../../types/hubInterfaces';
import { InitialState } from '../../../../types/hubTypes';
import { RESCHEDULE_PAGE_REQUEST_TYPES } from '../../../../util/constants';
import { filterUpcomingForEnrolment, getRescheduleSessionRoute } from '../../../../util/helper';
import { canVoucherBeIssued } from '../../../../util/helpers';
import { PATH_HUB_HOME } from '../../../../util/pagePath';
import { usePageHeader } from '../../../../v2/app-shell/usePageHeader';
import CreditPolicy from '../CreditPolicy/CreditPolicy';
import VoucherInfo from '../VoucherInfo/VoucherInfo';
import { SessionSummary } from './SessionSummary';

interface Props {
  formFlowProps: FormFlowProps;
}

const CancelSessionConfirm = ({ formFlowProps }: Props) => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const dispatch = useDispatch();
  const upcomingSessions: Array<Session> = useSelector(getUpcomingSessions);
  const { isLoading: cancelLoading, fetchError: cancelFailed } = useSelector(
    (state: InitialState) => state.ui.apiState.hubContactPage
  );
  const sessionToSkip = upcomingSessions.find((sess) => sess.studentSessionId === sessionId);
  const {
    data: sessionPolicy,
    isLoading: isPolicyLoading,
    error: policyError,
  } = useGetSessionPolicy({ sessionId: sessionToSkip?.studentSessionId });
  const { setHeader } = usePageHeader();

  useEffect(() => {
    setHeader({
      label: `Cancel session (inform ${sessionToSkip?.tutor?.split(' ')[0]})`,
      showIcon: false,
      showBackButton: true,
    });
  }, [sessionToSkip?.tutor, setHeader]);

  useRedirectInvalidSession(sessionToSkip);

  const getNextSession = () => {
    if (!sessionToSkip) {
      return null;
    }
    const filteredSessions = filterUpcomingForEnrolment(sessionToSkip.uniqueEnrolmentId, upcomingSessions);
    return filteredSessions.find((sess) => sess.studentSessionId !== sessionToSkip.studentSessionId);
  };

  const sessionBookedWithVoucher = sessionToSkip.studentSessionType === STUDENT_SESSION_TYPE_KEYS.MAKE_UP;
  const isPaid = sessionToSkip.paymentStatus === PAYMENT_STATUS.PAID;
  const voucherCanBeIssued =
    !sessionBookedWithVoucher &&
    sessionPolicy &&
    sessionToSkip &&
    isPaid &&
    canVoucherBeIssued(sessionPolicy, sessionToSkip);

  const onSkipConfirm = () => {
    dispatch(
      submitCaseToSF({
        action: RESCHEDULE_PAGE_REQUEST_TYPES.CANCEL,
        studentSession: sessionId,
        uniqueEnrolmentId: sessionToSkip.uniqueEnrolmentId,
        onSubmitSuccess: () => {
          voucherCanBeIssued
            ? formFlowProps.jump(2, { sessionToSkip, sessionPolicy })
            : formFlowProps.next({ sessionToSkip, nextSession: getNextSession() });
          dispatch(removeCancelledSession({ cancelledSession: sessionToSkip }));
        },
      })
    );
  };

  const renderActionButton = () => {
    if (sessionPolicy?.reschedule?.isAllowed) {
      return (
        <AnchorButton
          className="mt-7 block"
          size="lg"
          href={getRescheduleSessionRoute(sessionToSkip.studentSessionId)}
          target="self"
        >
          Reschedule session instead
        </AnchorButton>
      );
    } else {
      return (
        <LinkButton appearance="reverse" className="mt-7 block" size="sm" to={PATH_HUB_HOME}>
          Home
        </LinkButton>
      );
    }
  };

  /*
    Caveat:
    The session policy api's cancelSession.isAllowed attribute shouldn't be used as a determinant 
    to whether the session can be cancelled or not.

    The only instance where the CancelSessionConfirm cancel session isn't rendered is if the session's policy's isAllowed is false
    and the session hasn't been paid for yet.
    Thus, regardless if isAllowed is true or false, as long as it has been paid for, the session can be cancelled.
  */
  const isOneOffRescheduled = sessionToSkip?.tutorRescheduleRequestStatusC === 'Approved';
  return (
    <EnrolmentLayout
      sectionHeading={`${sessionToSkip.student} ${sessionToSkip.subject}`}
      mainHeading={`Cancel session (inform ${sessionToSkip.tutor})`}
      showEnrolmentStatus={false}
      showSummerModeEnrolmentStatus={false}
      enrolmentId={sessionToSkip.uniqueEnrolmentId}
    >
      {cancelLoading || isPolicyLoading ? (
        <div className="mx-auto my-10">
          <Loader />
        </div>
      ) : (
        <ContainerLayout>
          <IntentIntro className="!mb-0">
            Let {sessionToSkip?.tutor?.split(' ')[0]} know you won't be attending this session
          </IntentIntro>
          {policyError && (
            <Alert
              className="my-2"
              type="error"
              body={`There was an error retrieving this session's policy. ${policyError}`}
            />
          )}
          {cancelFailed && (
            <Alert body="" type="error">
              There was an error with your cancel session request.
            </Alert>
          )}
          {voucherCanBeIssued ||
          sessionBookedWithVoucher ||
          (sessionPolicy?.cancelSession?.isAllowed && sessionToSkip.paymentStatus !== PAYMENT_STATUS.PAID) ? null : (
            <CreditPolicy sessionToSkip={sessionToSkip} sessionPolicy={sessionPolicy} isLoading={isPolicyLoading} />
          )}
          {voucherCanBeIssued && (
            <VoucherInfo
              body={
                <>
                  <p className="mt-2.5">
                    This session falls during a school holiday period. When you cancel this session, you'll receive a
                    Cluey Voucher that can be used to book a make-up session at a time that suits you next term.
                  </p>
                  <p className="mt-2.5">
                    {`Your voucher will be valid for ${sessionPolicy.voucher.voucherExpiryDays} days from the original session date and can be used across any active enrolment.`}
                  </p>
                </>
              }
            />
          )}
          {sessionBookedWithVoucher && (
            <VoucherInfo
              title="Cluey Voucher sessions are not reissued if cancelled"
              alertType="warning"
              body={
                <>
                  <p className="mt-2.5">
                    You're cancelling a make-up session that was booked using a Cluey Voucher. If you continue, the
                    voucher will be forfeited and cannot be used again.
                  </p>
                </>
              }
            />
          )}
          {!voucherCanBeIssued &&
          !sessionBookedWithVoucher &&
          sessionToSkip.paymentStatus !== PAYMENT_STATUS.PAID &&
          !sessionPolicy?.cancelSession?.isAllowed ? (
            renderActionButton()
          ) : (
            <SessionSummary
              sessionPolicy={sessionPolicy}
              sessionToSkip={sessionToSkip}
              skipFailed={cancelFailed}
              onSkipConfirm={onSkipConfirm}
              isOneOffRescheduled={isOneOffRescheduled}
            />
          )}
        </ContainerLayout>
      )}
    </EnrolmentLayout>
  );
};

export default CancelSessionConfirm;
